import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import postSalonDataAndRefreshTokens from 'actions/salon/postSalonDataAndRefreshTokens.action';
import slickLogo from 'assets/images/logos/slick-logo-text-white.png';
import generateDefaultSalonData from 'bl/salon/generateDefaultSalonData.bl';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import EnumeratedInput from 'components/form/EnumeratedInput.component';
import FormField from 'components/form/FormField.component';
import IntegerInput from 'components/form/IntegerInput.component';
import TextInput from 'components/form/TextInput.component';
import { FormCard } from 'components/onboarding/FormCard.component';
import { OnboardingLayout } from 'components/onboarding/OnboardingLayout.component';
import { SalonSetupLocationFields } from 'components/onboarding/SalonSetupLocationFields.component';
import { StepIndicator } from 'components/onboarding/StepIndicator.component';
import { STATES_MAP, UK_COUNTIES } from 'constants/address/counties.const';
import CASING from 'constants/casing.const';
import LINK from 'constants/link.const';
import QST from 'constants/quickSetupType.const';
import BUSINESS_TYPE from 'constants/salon/businessType.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectOnboardingCompleted, selectOnboardingError, selectOnboardingIsPosting } from 'selectors/onboarding.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import { selectCognitoAttributeEmail, selectCognitoAttributeName, selectCognitoAttributePhone, selectCognitoUserObject } from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromPropsToQuery } from 'utils/converters/props/path.util';
import { getIntlCountryCode } from 'utils/intl/locale.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import batchValidate from 'utils/predicates/batchValidate.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import isFullString from 'utils/predicates/string/isFullString.util';
import finishOnboarding from 'utils/salons/finishOnboarding.util';
import byText from 'utils/sorters/byText.util';
import { getGtmPagePath, pushToDataLayer } from 'utils/tracking';

import PopupModalContainer from 'common/PopupModal';
import { normalizeEmail } from 'lib/normalize';

import CN from './SalonSetupPage.module.scss';

const CTAG = 'SalonSetupPage';
const L = logger(CTAG);

const MANAGE_BOOKINGS = Object.freeze(
  [
    'Frivey / Salon App',
    'Ovatu',
    'Meevo',
    'Booker',
    'Goldie',
    'Booksy',
    'Boulevard',
    'DaySmart',
    'Fresha',
    'GlossGenius',
    'MangoMint',
    'Nearcut',
    'Other',
    'Pen & paper',
    'Vagaro',
    'Phorest',
    'Rosy',
    'SalonIQ',
    'Shortcuts',
    'Timely',
    'Square Appointments',
    'Squire',
    'Treatwell',
    'Zenoti',
  ].sort(byText({ casing: CASING.ignore })),
);

const F = Object.freeze({
  name: 'name',
  mail: 'email',
  city: 'city',
  state: 'state',
  adl1: 'address_line_1',
  adl2: 'address_line_2',
  post: 'postcode',
  size: 'size',
  book: 'manage_booking_method',
});

const VALIDATOR = Object.freeze({
  [F.name]: validateName,
  [F.mail]: validateEmail,
  [F.size]: ({ value }) => Number.isSafeInteger(value) && value > 0,
  [F.book]: ({ value }) => isFullString(value),
  [F.adl1]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
  [F.city]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
  [F.post]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
  [F.state]: ({ value }) => isFullString(value),
});

const BUSINESS_TYPE_BY_SALON_TYPE = {
  [QST.hair]: BUSINESS_TYPE.salonHair,
  [QST.beauty]: BUSINESS_TYPE.salonBeauty,
};

const determineQuickSetupType = (props) => getAnyOrVoid('location.state.salonType', props) || fromPropsToQuery(props).salonType;

const COUNTRIES_WITH_STATES = ['US', 'AE'];

export const SalonSetupPage = (props) => {
  const dispatch = useDispatch();

  const cognitoUser = useSelector(selectCognitoUserObject);
  const cognitoName = useSelector(selectCognitoAttributeName);
  const cognitoPhone = useSelector(selectCognitoAttributePhone);
  const cognitoEmail = useSelector(selectCognitoAttributeEmail);
  const isPosting = useSelector(selectOnboardingIsPosting);
  const isOnboardingCompleted = useSelector(selectOnboardingCompleted);
  const onboardingError = useSelector(selectOnboardingError);

  const { t } = useTranslation();

  const nav = navFor(CTAG, props);

  const [errorMessage, setErrorMessage] = useStateWhileMounted(onboardingError);
  const [fields, setFields] = useStateWhileMounted(generateDefaultSalonData({ phone: cognitoPhone, email: cognitoEmail }));
  const [locationData, setLocationData] = useState({ country: '', timezone: '' });
  const { val: triedToSubmit, yay: showFooterSubmit, nay: hideFooterSubmit } = useTip(false);
  const { val: isSubmitting, yay: startSubmitting, nay: stopSubmitting } = useTip(false);

  const isBatchValidated = batchValidate({ validators: VALIDATOR, fields });
  const isLocationValid = isFullString(locationData?.country);
  const isNextEnabled = fromAnyToBool(!isPosting) && isBatchValidated && isLocationValid;

  const userName = fromAnyToTrimmed(cognitoName) || t('register.salonSetup.newBestFriend');
  const namePlaceholder = t('register.salonSetup.namePlaceholder', { userName });

  const intlCountryCode = getIntlCountryCode();
  const selectedCountry = isLocationValid ? locationData?.country : intlCountryCode || 'GB';
  const selectedLocationOptions = STATES_MAP?.[selectedCountry] || UK_COUNTIES;

  const isCountryWithStates = COUNTRIES_WITH_STATES.includes(selectedCountry);

  const handleDismiss = useCallback(() => void setErrorMessage(S.empty), [setErrorMessage]);

  const handleChange = useCallback(
    (ev) => {
      const { name: fieldName, raw, number } = asObject(ev);

      // little massaging of the input values to get the desired type or canonical representation
      let v = raw;
      if (fieldName === F.mail) v = normalizeEmail(raw);
      if (fieldName === F.size) v = number;

      if (fields[fieldName] === v) return;

      setFields({ ...fields, [fieldName]: v });
    },
    [fields, setFields],
  );

  const handleValidate = useCallback(
    (ev) => {
      const { name: fieldName, dirty, raw: value } = asObject(ev);
      const validate = VALIDATOR[fieldName];

      const VALIDATION_MESSAGE = {
        [F.name]: 'common.errors.validation.noSalonName',
        [F.mail]: 'common.errors.validation.notValidEmail',
        [F.book]: 'common.errors.validation.noProvider',
        [F.adl1]: 'common.errors.validation.noAddress',
        [F.city]: 'common.errors.validation.noCity',
        [F.post]: isCountryWithStates ? 'common.errors.validation.noZipCode' : 'common.errors.validation.noPostcode',
        [F.state]: isCountryWithStates ? 'common.errors.validation.noSelectedState' : 'common.errors.validation.noSelectedCounty',
      };

      // no point validating if there wasn't even a single change
      if (!dirty) return null;
      if (isNotFn(validate)) return null;

      // return null to clear the error from before
      return { error: validate({ value }) ? null : t(VALIDATION_MESSAGE[fieldName]) };
    },
    [isCountryWithStates, t],
  );

  const handleSubmit = () => {
    if (!isNextEnabled) return;

    // NOTE: only quick setups are allowed atm
    const quickSetupType = determineQuickSetupType(props);
    if (!quickSetupType) return;

    startSubmitting();

    const options = {
      quick: true,
      type: quickSetupType,
      data: {
        ...fields,
        business_type: BUSINESS_TYPE_BY_SALON_TYPE[quickSetupType],
        country: locationData?.country,
        timezone: locationData?.timezone,
      },
    };

    L.unignore('handleSubmit()', options, dispatch(postSalonDataAndRefreshTokens(options)))
      .then(async (result) => {
        await finishOnboarding({ cognitoUser });

        const salonId = L.debug('handleSubmit()', 'salonId:', getAnyOrVoid('response.id', result));

        if (salonId) {
          pushToDataLayer('signup_step3_submit', { step_number: 3, page_path: getGtmPagePath() });
          nav.push({ pathname: `/salon/${salonId}/day/today` });
        } else {
          setErrorMessage(t('common.errors.generic'));
          stopSubmitting();
        }
      })
      .catch((err) => {
        L.debug('handleSubmit()', 'error:', err);
        setErrorMessage(err?.message || t('common.errors.generic'));
        stopSubmitting();
      });
  };

  const submitSalon = () => {
    if (!isNextEnabled) {
      showFooterSubmit();
      return;
    }

    hideFooterSubmit();
    handleSubmit();
  };

  // from componentDidMount, it scrolled the window to focus on the input form
  useEffect(() => void window.scrollTo(0, 0), []);

  // from willReceiveProps, was used to skip this step if already completed
  useEffect(() => {
    if (isOnboardingCompleted) {
      nav.push({ pathname: LINK.login });
    }
    return;
  }, [isOnboardingCompleted, nav]);

  L.debug('()', { isNextEnabled });

  return (
    <div data-bem="SalonSetupPage" className={CN.component}>
      {errorMessage && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessage} buttonText={t('common.buttons.okay')} buttonCallback={handleDismiss} />}

      <OnboardingLayout mobileHeader={<img src={slickLogo} alt="Slick" className={CN.logo} />}>
        <FormCard
          header={
            <div>
              <p className={CN.greeting}>
                {t('register.salonSetup.greeting', { userName })}
                {S.space}
                {S.wavingHand}
              </p>
              <h2 className={CN.title}>{t('register.salonSetup.pageTitle')}</h2>
            </div>
          }
          submitButtonLabel={t('register.salonSetup.submitButton')}
          isSubmitDisabled={!isNextEnabled}
          isLoading={isPosting || isSubmitting}
          onSubmit={submitSalon}
        >
          <StepIndicator currentStep={3} />

          <div data-bem="SalonSetupPage__name-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.name}
              value={fields[F.name]}
              label={t('register.salonSetup.nameLabel')}
              placeholder={namePlaceholder}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__email-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.mail}
              value={fields[F.mail]}
              label={t('register.salonSetup.emailLabel')}
              placeholder={t('register.salonSetup.emailPlaceholder')}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>

          <SalonSetupLocationFields onChange={setLocationData} />

          <div data-bem="SalonSetupPage__adl1-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.adl1}
              value={fields[F.adl1]}
              label={t('register.salonSetup.address1Label')}
              placeholder={t('register.salonSetup.address1Placeholder')}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__adl2-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.adl2}
              value={fields[F.adl2]}
              label={t('register.salonSetup.address2Label')}
              placeholder={t('register.salonSetup.address2Placeholder')}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__city-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.city}
              value={fields[F.city]}
              label={t('register.salonSetup.cityLabel')}
              placeholder={t('register.salonSetup.cityPlaceholder')}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__state-row" className={CN.formField}>
            <FormField
              input={EnumeratedInput}
              name={F.state}
              value={fields[F.state]}
              label={isCountryWithStates ? t('register.salonSetup.stateLabel') : t('register.salonSetup.countyLabel')}
              placeholder={isCountryWithStates ? t('register.salonSetup.statePlaceholder') : t('register.salonSetup.countyPlaceholder')}
              options={selectedLocationOptions}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__postcode-row" className={CN.formField}>
            <FormField
              input={TextInput}
              name={F.post}
              value={fields[F.post]}
              label={isCountryWithStates ? t('register.salonSetup.zipcodeLabel') : t('register.salonSetup.postcodeLabel')}
              placeholder={isCountryWithStates ? t('register.salonSetup.zipcodePlaceholder') : t('register.salonSetup.postcodePlaceholder')}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          <div data-bem="SalonSetupPage__staff-row" className={CN.formField}>
            <FormField input={IntegerInput} name={F.size} value={fields[F.size]} label={t('register.salonSetup.bookableStaffNumberLabel')} onChange={handleChange} onValidate={handleValidate} />
          </div>
          <div data-bem="SalonSetupPage__bookings-row" className={CN.formField}>
            <FormField
              input={EnumeratedInput}
              label={t('register.salonSetup.manageBookingsLabel')}
              placeholder={t('register.salonSetup.manageBookingsPlaceholder')}
              name={F.book}
              value={fields[F.book]}
              options={MANAGE_BOOKINGS}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          </div>
          {triedToSubmit && !isNextEnabled && <div className={CN.error}>{t('register.salonSetup.completeAllFieldsMsg')}</div>}
        </FormCard>
      </OnboardingLayout>

      {(isPosting || isSubmitting) && !errorMessage && <div data-bem="SalonSetupPage__load-blocker" className={CN.loadBlocker} />}
    </div>
  );
};
